import React, { useState, useEffect } from "react";
import { Input, InputGroup, InputLeftElement, Flex } from "@chakra-ui/react";
import { SearchIcon } from "@root/assets/icons";
import { useDebouncedCallback } from "use-debounce";

interface SearchInputProps {
  placeholder?: string;
  width?: string | number;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  debounceDelay?: number;
  minCharacters?: number;
  [key: string]: any;
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = "Search...",
  width = "100%",
  value: controlledValue,
  onChange,
  onKeyDown,
  debounceDelay = 500,
  minCharacters = 3,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState(controlledValue || "");

  // Sync internal value with controlled value when it changes externally
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  // Debounced callback that only triggers onChange when value has at least minCharacters
  const debouncedOnChange = useDebouncedCallback(
    (value: string) => {
      if (onChange && value.length >= minCharacters) {
        // Create a synthetic event-like object
        const syntheticEvent = {
          target: { value },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    },
    debounceDelay
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);

    // If value is less than minCharacters, don't trigger onChange
    if (newValue.length >= minCharacters) {
      debouncedOnChange(newValue);
    } else if (newValue.length === 0 && onChange) {
      // If cleared, trigger onChange immediately with empty value
      onChange(e);
    }
  };

  return (
    <InputGroup display={"flex"} alignItems={"center"} width={width}>
      <InputLeftElement pointerEvents="none" height="100%">
        <Flex align="center" height="100%">
          <SearchIcon style={{ width: 20, height: 20 }} />
        </Flex>
      </InputLeftElement>
      <Input
        type="text"
        placeholder={placeholder}
        value={internalValue}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        borderRadius="12px"
        borderColor="#CDD6DC"
        borderWidth="1px"
        fontSize="16px"
        textColor={"#798692"}
        fontWeight={400}
        color="#798692"
        pl="48px"
        height="44px"
        bg="white"
        _placeholder={{ color: "#798692", fontWeight: 400, fontSize: "16px" }}
        {...rest}
      />
    </InputGroup>
  );
};

export default SearchInput;
